import type { GentleTaskDatabase } from '@/data/db/database';
import type { MirrorChange, MirrorSnapshot } from './mirror-bridge';
import type { DirtyKey } from './mirror-middleware';
import { noteImageSchema } from '@/data/db/note-image.schema';
import { collectNoteImageIds, parseNoteDoc } from '@/features/notes/note-content';
import { clearNoteDraft, listDraftNoteIds } from '@/features/notes/note-draft';
import { removeSupersededNoteImageFiles } from '@/data/repositories/note-image-repository';

/**
 * Dexie 的列 ↔ 鏡像的變更之間的轉換。
 *
 * 附件的原圖與縮圖走 **base64**，不是 typed array。
 * Tauri 的 IPC 會把 `Uint8Array` 序列化成 JSON 的數字陣列，8 MiB 的圖會膨脹成
 * 三十幾 MB 的字串；base64 只多三分之一。附件是不可變的，只在新增與刪除時鏡像
 * （6.3、決議 25），所以這個成本一張圖只付一次。
 */

/** 附件的 blob 不能直接 JSON.stringify，metadata 另外挑出來。 */
const ATTACHMENT_META_KEYS = [
  'id',
  'ownerType',
  'ownerId',
  'fileName',
  'mimeType',
  'originalSize',
  'size',
  'width',
  'height',
  'sha256',
  'createdAt',
] as const;

function attachmentMetaJson(row: Record<string, unknown>): string {
  const meta: Record<string, unknown> = {};
  for (const key of ATTACHMENT_META_KEYS) meta[key] = row[key];
  return JSON.stringify(meta);
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  // 一次全部丟給 fromCharCode 在大圖上會爆 call stack，所以分塊。
  const CHUNK = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }
  return btoa(binary);
}

export function base64ToBlob(value: string, mimeType: string): Blob {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mimeType });
}

/** 鏡像不需要知道的表：它們只是本機 UI 狀態或衍生資料。 */
const SKIPPED_TABLES = new Set<string>([]);

function tableOf(db: GentleTaskDatabase, name: string) {
  return db.tables.find((table) => table.name === name);
}

/**
 * 把髒掉的 id 重讀成鏡像變更。查不到的一律當成刪除——
 * 這也涵蓋了「交易回滾了，所以那一筆其實沒被寫進去」的情況。
 */
export async function collectChanges(
  db: GentleTaskDatabase,
  dirty: readonly DirtyKey[],
): Promise<MirrorChange[]> {
  const changes: MirrorChange[] = [];

  for (const key of dirty) {
    if (SKIPPED_TABLES.has(key.table)) continue;
    const table = tableOf(db, key.table);
    if (!table) continue;

    const row = (await table.get(key.id)) as Record<string, unknown> | undefined;
    if (!row) {
      changes.push({ table: key.table, id: key.id, deleted: true });
      continue;
    }
    changes.push(await toChange(key.table, key.id, row));
  }

  return changes;
}

export async function toChange(
  table: string,
  id: string,
  row: Record<string, unknown>,
): Promise<MirrorChange> {
  if (table === 'attachments') {
    const blob = row['blob'];
    const thumbnail = row['thumbnailBlob'];
    return {
      table,
      id,
      json: attachmentMetaJson(row),
      blobBase64: blob instanceof Blob ? await blobToBase64(blob) : '',
      thumbnailBase64: thumbnail instanceof Blob ? await blobToBase64(thumbnail) : '',
    };
  }
  return { table, id, json: JSON.stringify(row) };
}

/** 整個資料庫的快照，供重建鏡像使用。 */
export async function collectAll(db: GentleTaskDatabase): Promise<MirrorChange[]> {
  const changes: MirrorChange[] = [];

  for (const table of db.tables) {
    if (SKIPPED_TABLES.has(table.name)) continue;
    const rows = (await table.toArray()) as Array<Record<string, unknown>>;
    for (const row of rows) {
      const id = row['id'];
      if (typeof id !== 'string') continue;
      changes.push(await toChange(table.name, id, row));
    }
  }

  return changes;
}

export interface RestoreReport {
  tables: Record<string, number>;
  attachments: number;
  cleanupWarnings?: string[];
}

/**
 * 把鏡像的快照寫回 Dexie。
 *
 * **破壞性**：整個清空後再寫。呼叫端要負責先確認（空庫自動、非空要三步確認，第 8 節）。
 */
export async function restoreFromSnapshot(
  db: GentleTaskDatabase,
  snapshot: MirrorSnapshot,
): Promise<RestoreReport> {
  const report: RestoreReport = { tables: {}, attachments: 0 };

  // 附件的 blob 要在交易外先解出來：atob 與 Blob 不是 IndexedDB request，
  // 在交易裡 await 它們會讓 Dexie 判定交易閒置而自動 commit
  // （import.ts 已經踩過同一個坑）。
  const attachments = snapshot.attachments.map((attachment) => {
    const meta = JSON.parse(attachment.json) as Record<string, unknown>;
    const mimeType = typeof meta['mimeType'] === 'string' ? meta['mimeType'] : 'image/webp';
    return {
      ...meta,
      blob: base64ToBlob(attachment.blobBase64, mimeType),
      thumbnailBlob: base64ToBlob(attachment.thumbnailBase64, mimeType),
    };
  });

  const parsed = new Map<string, Array<Record<string, unknown>>>();
  for (const [name, rows] of Object.entries(snapshot.tables)) {
    parsed.set(
      name,
      rows.map((row) => JSON.parse(row) as Record<string, unknown>),
    );
  }

  // A .db is sufficient for automatic recovery on this machine only when its
  // external PNGs still exist. A portable backup must be the complete ZIP.
  const noteImages = (parsed.get('noteImages') ?? []).map((row) => noteImageSchema.parse(row));
  const ids = new Set(noteImages.map((image) => image.id));
  for (const note of parsed.get('notes') ?? []) {
    for (const id of collectNoteImageIds(parseNoteDoc(String(note['contentJson'])))) {
      if (!ids.has(id)) throw new Error('資料庫缺少筆記圖片資訊，請從完整 ZIP 還原');
    }
  }
  if (noteImages.length > 0) {
    const { readNoteImageFile } = await import('@/platform/note-image-files');
    const { sha256Hex } = await import('@/features/attachments/process-image');
    for (const image of noteImages) {
      const bytes = await readNoteImageFile(image.noteId, image.id);
      if (
        bytes.byteLength !== image.size ||
        (await sha256Hex(new Blob([bytes as BlobPart]))) !== image.sha256
      ) {
        throw new Error(`圖片 ${image.fileName} 遺失或內容不符，請從完整 ZIP 還原`);
      }
    }
  }

  const previousImages = await db.noteImages.toArray();
  await db.transaction('rw', db.tables, async () => {
    await Promise.all(db.tables.map((table) => table.clear()));

    for (const [name, rows] of parsed) {
      const table = tableOf(db, name);
      if (!table || rows.length === 0) continue;
      await table.bulkPut(rows);
      report.tables[name] = rows.length;
    }

    if (attachments.length > 0) {
      await db.attachments.bulkPut(attachments as never);
      report.attachments = attachments.length;
    }
  });

  for (const id of listDraftNoteIds()) clearNoteDraft(id);
  const warnings = await removeSupersededNoteImageFiles(previousImages, noteImages);
  if (warnings.length > 0) report.cleanupWarnings = warnings;

  return report;
}
